import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, concatMap, from, map, of, take, tap } from 'rxjs';

import { VendorOperatingStatesForm } from '../classes/form-groups/vendor-operating-states-form';
import { Vendor } from '../classes/models';
import { OperatingStateOption } from '../interfaces/operating-state-option';
import { ConfirmModalComponent } from '../modals/confirm-modal/confirm-modal.component';
import { vendorsService } from '../shared/vive-api/vendors.service';
import { LOAD_BALANCER_TIMEOUT_MS } from '../shared/vive-api/vive-api.config';
import { VendorService } from './vendor.service';

@Injectable({
  providedIn: 'root'
})
export class VendorTradeStateService {
  constructor(
    private VendorService: VendorService,
    private ModalService: NgbModal
  ) {}

  /**
   * The vendor's operating states, plus which of their linked clients operate
   * in each state (SubNationalDivisionId -> client legal names). Only those
   * states drive Trade License requirements.
   */
  getOperatingStates(VendorId: number): Observable<{
    operatingStates: OperatingStateOption[];
    clientNamesByDivisionId: Map<number, string[]>;
  }> {
    return this.VendorService.getVendorV2(VendorId, {
      attributes: JSON.stringify(['id']),
      VendorTradeState: JSON.stringify({
        SubNationalDivision: {
          attributes: ['id', 'name', 'abbreviation']
        }
      }),
      CompanyVendor: JSON.stringify({
        attributes: ['id', 'CompanyId', 'VendorId'],
        Company: {
          attributes: ['id', 'legalName'],
          CompanyOperationState: {
            attributes: ['id', 'CompanyId', 'SubNationalDivisionId']
          }
        }
      })
    }).pipe(
      map(({ VendorTradeStates, CompanyVendors }: Vendor) => {
        const clientNamesByDivisionId = new Map<number, string[]>();

        (CompanyVendors || []).forEach(({ Company }) => {
          const legalName = Company?.legalName;

          if (!legalName) {
            return;
          }

          (Company.CompanyOperationStates || []).forEach(
            ({ SubNationalDivisionId }) => {
              if (!SubNationalDivisionId) {
                return;
              }

              clientNamesByDivisionId.set(SubNationalDivisionId, [
                ...(clientNamesByDivisionId.get(SubNationalDivisionId) ?? []),
                legalName
              ]);
            }
          );
        });

        const operatingStates: OperatingStateOption[] = [];

        (VendorTradeStates || []).forEach(({ SubNationalDivision }) => {
          const { id, name, abbreviation } = SubNationalDivision ?? {};

          if (id && name && abbreviation) {
            operatingStates.push({
              id,
              name,
              abbreviation,
              clientNames: clientNamesByDivisionId.get(id) ?? []
            });
          }
        });

        return { operatingStates, clientNamesByDivisionId };
      })
    );
  }

  setSavedOperatingStates(
    OperatingStatesForm: VendorOperatingStatesForm,
    operatingStates: OperatingStateOption[]
  ): void {
    const sorted = [...operatingStates].sort((left, right) =>
      left.name.localeCompare(right.name)
    );

    OperatingStatesForm.setValue({
      SubNationalDivisions: sorted,
      savedSubNationalDivisions: sorted
    });
    OperatingStatesForm.markAsPristine();
  }

  getOperatingStateChanges(OperatingStatesForm: VendorOperatingStatesForm): {
    added: OperatingStateOption[];
    removed: OperatingStateOption[];
  } {
    const { SubNationalDivisions, savedSubNationalDivisions } =
      OperatingStatesForm.getRawValue();
    const savedIds = new Set(savedSubNationalDivisions.map(({ id }) => id));
    const selectedIds = new Set(SubNationalDivisions.map(({ id }) => id));

    return {
      added: SubNationalDivisions.filter(({ id }) => !savedIds.has(id)),
      removed: savedSubNationalDivisions.filter(
        ({ id }) => !selectedIds.has(id)
      )
    };
  }

  /**
   * Warns before a save that changes a state a linked client operates in: the
   * client's license requirements change, so a Passed Trades & Licenses step
   * goes back to Reviewing. Emits true straight away when nothing is affected.
   */
  confirmOperatingStateChanges(
    OperatingStatesForm: VendorOperatingStatesForm
  ): Observable<boolean> {
    const { added, removed } =
      this.getOperatingStateChanges(OperatingStatesForm);
    const clientNames = [
      ...new Set([...added, ...removed].flatMap((state) => state.clientNames))
    ].sort((left, right) => left.localeCompare(right));

    if (clientNames.length === 0) {
      return of(true);
    }

    const listNames = (names: string[]): string =>
      names.length < 2
        ? names.join('')
        : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
    const changes = [
      added.length > 0
        ? `adding ${listNames(added.map(({ name }) => name))}`
        : null,
      removed.length > 0
        ? `removing ${listNames(removed.map(({ name }) => name))}`
        : null
    ]
      .filter((change): change is string => change !== null)
      .join(' and ');

    const { componentInstance, closed } = this.ModalService.open(
      ConfirmModalComponent,
      { backdrop: 'static', modalDialogClass: 'confirm-modal' }
    );

    Object.assign(componentInstance, {
      title: 'Trades & Licenses will be re-reviewed',
      displayText: `You're ${changes}. For ${listNames(clientNames)}, Trades & Licenses will move back to Reviewing if it has passed, so the required licenses can be re-checked.`,
      continueButtonText: 'Save changes'
    });

    return closed.pipe(take(1));
  }

  /**
   * Applies the admin's pending changes on top of the vendor's current states,
   * re-read at save time rather than trusting what the page loaded: a Client
   * Offices save earlier in the same Save can add states too.
   *
   * Writes through the vendor PATCH (the path registration uses), which
   * replaces every VendorTradeState row from the comma-separated names and
   * keeps Vendor.tradeStates in step.
   */
  saveOperatingStates(
    VendorId: number,
    OperatingStatesForm: VendorOperatingStatesForm
  ): Observable<OperatingStateOption[]> {
    const { added, removed } =
      this.getOperatingStateChanges(OperatingStatesForm);

    if (added.length === 0 && removed.length === 0) {
      return of(OperatingStatesForm.controls.savedSubNationalDivisions.value);
    }

    const removedIds = new Set(removed.map(({ id }) => id));

    return this.getOperatingStates(VendorId).pipe(
      concatMap(({ operatingStates }) => {
        const finalById = new Map(
          operatingStates.map((state) => [state.id, state])
        );

        added.forEach((state) => finalById.set(state.id, state));
        removedIds.forEach((id) => finalById.delete(id));

        const final = [...finalById.values()];
        const tradeStates = final
          .map(({ name }) => name)
          .sort((left, right) => left.localeCompare(right))
          .join(',');

        return from(
          vendorsService.updateOne({
            pathParams: { vendorId: VendorId },
            data: { tradeStates: tradeStates || null },
            options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
          })
        ).pipe(map(() => final));
      }),
      tap((final) => this.setSavedOperatingStates(OperatingStatesForm, final))
    );
  }
}
